import { useState } from 'react';
import {
  PrimaryButton,
  SecondaryButton,
  TextButton,
} from '@/lib';
import type { ApiClients } from '@/api/client';
import { useDeleteUserPolicy, usePutUserPolicy } from './queries';

export type UserOverridesProps = {
  clients: ApiClients;
  /** Map of userId → caps, sourced from PolicyResponse.user_overrides. */
  overrides: Record<string, string[]>;
  /** Every cap the matrix knows about, for the picker. */
  allCaps: string[];
};

/**
 * Per-user policy overrides section. Lives below the per-role matrix
 * in S15. Per-user overrides win over the role override and the
 * static matrix — most-specific wins. Admin-floor (no admin-only caps
 * to non-admin users) is enforced server-side; client UI just lists.
 */
export function UserOverrides({ clients, overrides, allCaps }: UserOverridesProps) {
  const put = usePutUserPolicy(clients);
  const del = useDeleteUserPolicy(clients);
  const [adding, setAdding] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [newCaps, setNewCaps] = useState<Set<string>>(new Set());

  const entries = Object.entries(overrides).sort((a, b) => a[0].localeCompare(b[0]));

  const toggleCap = (cap: string) => {
    setNewCaps((prev) => {
      const next = new Set(prev);
      if (next.has(cap)) next.delete(cap);
      else next.add(cap);
      return next;
    });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserId.trim()) return;
    put.mutate(
      { userId: newUserId.trim(), capabilities: [...newCaps] },
      {
        onSuccess: () => {
          setNewUserId('');
          setNewCaps(new Set());
          setAdding(false);
        },
      },
    );
  };

  return (
    <section
      aria-label="Per-user overrides"
      className="flex flex-col gap-sp-3"
    >
      <div className="flex items-baseline justify-between">
        <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
          Per-user overrides
        </p>
        {!adding ? (
          <TextButton label="+ Add override" onClick={() => setAdding(true)} />
        ) : null}
      </div>

      {entries.length === 0 && !adding ? (
        <p className="text-fs-meta text-gray-700">
          No per-user overrides. Use "+ Add override" to grant a specific user a
          different cap set than their role's.
        </p>
      ) : null}

      {entries.length > 0 ? (
        <ul className="flex flex-col gap-sp-2">
          {entries.map(([userId, caps]) => (
            <li
              key={userId}
              className="flex items-start justify-between gap-sp-3 rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2"
            >
              <div className="min-w-0 flex-1">
                <p className="font-mono text-fs-body text-gray-900">{userId}</p>
                <ul className="mt-sp-1 flex flex-wrap gap-sp-1 font-mono text-fs-meta text-gray-700">
                  {caps.length === 0 ? (
                    <li className="italic text-gray-500">no caps (effectively locked out)</li>
                  ) : (
                    caps.map((c) => (
                      <li
                        key={c}
                        className="rounded-r-chip bg-gray-100 px-sp-2 py-sp-1"
                      >
                        {c}
                      </li>
                    ))
                  )}
                </ul>
              </div>
              <TextButton
                label={del.isPending ? 'Removing…' : 'Remove'}
                onClick={() => {
                  if (window.confirm(`Remove the per-user override for ${userId}?`)) {
                    del.mutate(userId);
                  }
                }}
                disabled={del.isPending}
              />
            </li>
          ))}
        </ul>
      ) : null}

      {adding ? (
        <form
          onSubmit={submit}
          className="flex flex-col gap-sp-3 rounded-r-default border border-gray-300 bg-gray-50 p-sp-4"
        >
          <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
            User ID
            <input
              required
              value={newUserId}
              onChange={(e) => setNewUserId(e.target.value)}
              placeholder="usr_…"
              className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 font-mono text-fs-body"
            />
          </label>
          <fieldset className="flex flex-col gap-sp-1">
            <legend className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
              Capabilities ({newCaps.size} selected)
            </legend>
            <p className="text-fs-meta text-gray-700">
              Empty selection clears the override (equivalent to remove).
            </p>
            <div className="grid max-h-64 gap-sp-1 overflow-y-auto rounded-r-default border border-gray-300 bg-gray-50 p-sp-2 sm:grid-cols-2">
              {allCaps.map((cap) => (
                <label
                  key={cap}
                  className="inline-flex items-center gap-sp-2 font-mono text-fs-meta text-gray-900"
                >
                  <input
                    type="checkbox"
                    checked={newCaps.has(cap)}
                    onChange={() => toggleCap(cap)}
                  />
                  {cap}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="flex items-center gap-sp-3">
            <PrimaryButton
              type="submit"
              label={put.isPending ? 'Saving…' : 'Save override'}
              disabled={put.isPending || !newUserId.trim()}
            />
            <SecondaryButton
              type="button"
              label="Cancel"
              onClick={() => {
                setAdding(false);
                setNewUserId('');
                setNewCaps(new Set());
              }}
            />
            {put.isError ? (
              <span role="alert" className="text-fs-meta text-critical">
                {String(put.error)}
              </span>
            ) : null}
          </div>
        </form>
      ) : null}
    </section>
  );
}
